import React from 'react';
import { Button } from './ui/button';
import { Lightbulb, Layers, Code, Rocket, TrendingUp } from 'lucide-react';

const Engagement = () => {
  const stages = [
    {
      icon: Lightbulb,
      title: 'Discovery',
      description: 'We understand your vision, goals, and technical requirements through detailed consultations.'
    },
    {
      icon: Layers,
      title: 'Architecture',
      description: 'Design scalable system architecture, choose optimal tech stack, and plan the development roadmap.'
    },
    {
      icon: Code,
      title: 'Build',
      description: 'Agile development with regular updates, code reviews, and testing throughout the process.'
    },
    {
      icon: Rocket,
      title: 'Launch',
      description: 'Deploy to production with comprehensive testing, security audits, and monitoring setup.'
    },
    {
      icon: TrendingUp,
      title: 'Scale',
      description: 'Ongoing support, optimization, feature additions, and infrastructure scaling as you grow.'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-[#0f1420] to-[#0a0e1a] relative overflow-hidden">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            How We Usually Engage
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            We've been designing and building beautiful digital products since 2018.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left: Process Steps */}
          <div className="space-y-6">
            {stages.map((stage, index) => (
              <div
                key={index}
                className="group flex gap-6 p-6 rounded-xl bg-gradient-to-r from-[#1a1f35]/50 to-transparent border border-white/5 hover:border-white/20 transition-all duration-300 hover:transform hover:translate-x-2"
              >
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#ff6b35] to-[#ff2e63] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <stage.icon size={24} className="text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2">{stage.title}</h3>
                  <p className="text-gray-400">{stage.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Right: Example Visualization */}
          <div className="space-y-6">
            {/* EARN Protocol Card */}
            <div className="bg-gradient-to-br from-[#1a1f35] to-[#0f1420] rounded-2xl border border-white/10 overflow-hidden">
              <div className="bg-gradient-to-r from-[#ff6b35]/20 to-[#ff2e63]/20 p-6 border-b border-white/10">
                <h3 className="text-2xl font-bold text-white mb-2">EARN Protocol</h3>
                <p className="text-gray-400">Decentralized Lending & Borrowing</p>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 rounded-lg p-4">
                    <div className="text-gray-400 text-xs mb-1">Total Supplied</div>
                    <div className="text-white text-xl font-bold">$12.5M</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4">
                    <div className="text-gray-400 text-xs mb-1">Total Borrowed</div>
                    <div className="text-white text-xl font-bold">$8.2M</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4">
                    <div className="text-gray-400 text-xs mb-1">Supply APY</div>
                    <div className="text-[#00d4ff] text-xl font-bold">7.25%</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4">
                    <div className="text-gray-400 text-xs mb-1">Borrow APY</div>
                    <div className="text-[#ff6b35] text-xl font-bold">4.80%</div>
                  </div>
                </div>
                <Button className="w-full bg-gradient-to-r from-[#ff6b35] to-[#ff2e63] hover:from-[#ff7b45] hover:to-[#ff3e73] text-white py-3 rounded-lg">
                  Connect Wallet
                </Button>
              </div>
            </div>

            {/* Statistics */}
            <div className="bg-gradient-to-br from-[#1a1f35] to-[#0f1420] rounded-2xl border border-white/10 p-6">
              <h4 className="text-xl font-bold text-white mb-4">Project Impact</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Active Users</span>
                  <span className="text-white font-bold">2,847</span>
                </div>
                <div className="w-full bg-white/5 rounded-full h-2">
                  <div className="bg-gradient-to-r from-[#00d4ff] to-[#0ea5e9] h-2 rounded-full" style={{ width: '75%' }}></div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Transactions</span>
                  <span className="text-white font-bold">15,234</span>
                </div>
                <div className="w-full bg-white/5 rounded-full h-2">
                  <div className="bg-gradient-to-r from-[#ff6b35] to-[#ff2e63] h-2 rounded-full" style={{ width: '90%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Engagement;